// src/ui/components/ShareModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import Card from "../../features/ui/Card";
import { ClipboardIcon } from "../../primatives/Icons"; // adjust path if needed

export default function ShareModal({ open, onClose }) {
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState("");

  const shareUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    return window.location.href;
  }, []);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    setCopied(false);
    setCopyError("");
  }, [open]);

  const onCopy = async () => {
    setCopyError("");
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 900);
    } catch {
      setCopyError("Copy failed. Please select and copy manually.");
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Refer a friend"
    >
      <Card className="relative w-full max-w-xl p-5">
        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="
            absolute right-3 top-3
            inline-flex h-8 w-8 items-center justify-center
            rounded-lg text-neutral-500
            hover:bg-neutral-100 hover:text-neutral-800
            dark:text-neutral-400 dark:hover:bg-neutral-900 dark:hover:text-neutral-100
          "
        >
          <span className="text-lg leading-none">&times;</span>
        </button>

        {/* Title */}
        <h3 className="mb-3 text-base font-semibold text-neutral-900 dark:text-neutral-50">
          Refer a friend
        </h3>

        {/* URL + embedded copy */}
        <div className="relative">
          <input
            readOnly
            value={shareUrl}
            className="
              w-full rounded-xl border border-neutral-200 bg-white
              pl-3 pr-11 py-2 text-sm text-neutral-800
              focus:outline-none focus:border-neutral-400
              dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-100
            "
            onFocus={(e) => e.target.select()}
            aria-label="Share URL"
          />

          <button
            type="button"
            onClick={onCopy}
            className="
              absolute right-1.5 top-1/2 -translate-y-1/2
              inline-flex h-8 w-8 items-center justify-center rounded-lg
              text-neutral-600 hover:bg-neutral-100
              dark:text-neutral-300 dark:hover:bg-neutral-900
            "
            aria-label="Copy to clipboard"
            title={copied ? "Copied" : "Copy"}
          >
            <ClipboardIcon className="h-4 w-4" />
          </button>
        </div>

        {copyError && (
          <div className="mt-2 text-sm text-red-600">{copyError}</div>
        )}

        {copied && (
          <div className="mt-2 text-xs text-neutral-500 dark:text-neutral-400">
            Link has been copied.
          </div>
        )}
      </Card>
    </div>
  );
}
